import { useState } from 'react';
import { useLocation } from 'wouter';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Mail, LogIn, ArrowLeft } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { requestPasswordRecovery } from '@/lib/authBootstrap';

export default function ForgotPasswordPage() {
  const [, setLocation] = useLocation();
  const [email, setEmail] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const normalizedEmail = email.trim().toLowerCase();

      // Store email in localStorage so /auth/verify can use it for OTP verification
      try {
        localStorage.setItem('pendingResetEmail', normalizedEmail);
      } catch (storageError) {
        console.warn('[FORGOT_PASSWORD] Failed to store pending reset email', storageError);
      }

      // Keep the existing redirect URL compatible with configured allow-lists.
      const { error } = await requestPasswordRecovery(normalizedEmail, `${window.location.origin}/auth/verify`);

      if (error) {
        console.error('[FORGOT_PASSWORD] Reset failed:', error.message);
        setError(error.message);
        toast({
          title: "Reset failed",
          description: error.message,
          variant: "destructive",
        });
      } else {
        console.log('[FORGOT_PASSWORD] Reset email sent successfully');
        toast({
          title: "Password reset email sent",
          description: "Check your inbox for instructions to reset your password.",
        });
        // Optionally redirect back to login after success
        setTimeout(() => setLocation('/'), 2000);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to send reset email';
      console.error('[FORGOT_PASSWORD] Unexpected error:', errorMessage);
      setError(errorMessage);
      toast({
        title: "Unexpected error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-6">
            <div className="flex items-center space-x-2">
              <LogIn className="h-8 w-8 text-blue-600" />
              <h1 className="text-2xl font-bold text-gray-900">ReActor</h1>
            </div>
          </div>
          <CardTitle className="text-2xl text-center">Reset your password</CardTitle>
          <CardDescription className="text-center">
            Enter your email address and we'll send you a link to reset your password
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4" noValidate data-form-type="other">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                disabled={isLoading}
                autoCorrect="off"
                autoCapitalize="none"
                spellCheck="false"
                data-1p-ignore
                data-lpignore="true"
                required
              />
            </div>

            {error && (
              <Alert variant="destructive">
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={isLoading || !email.trim()}
            >
              {isLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Sending reset email...
                </>
              ) : (
                <>
                  <Mail className="mr-2 h-4 w-4" />
                  Send reset email
                </>
              )}
            </Button>

            <div className="text-center">
              <Button
                variant="link"
                className="text-sm text-blue-600 hover:text-blue-500"
                onClick={() => setLocation('/')}
              >
                <ArrowLeft className="mr-1 h-4 w-4" />
                Back to login
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
